/**
 * Procedural ambient sound: no audio files, everything is synthesised with
 * WebAudio. A hollow describes its soundscape as a few beds (wind, a drone, a
 * rumble) plus random one-shot events (birds, crickets, chimes, crackles,
 * drips). Events can follow the day: `day` in update() is 0 at night, 1 by day.
 *
 * Browsers only allow audio after a user gesture, so call start() from one
 * (e.g. the click that captures the mouse).
 */
export type AmbientEvent = 'bird' | 'cricket' | 'chime' | 'crackle' | 'drip';

export interface AmbienceDef {
  /** Filtered-noise wind: loudness and brightness (low-pass cutoff, Hz). */
  wind?: { gain: number; cutoff: number };
  /** A soft pad of sine tones (Hz). */
  drone?: { freqs: number[]; gain: number };
  /** Deep brown-noise rumble. */
  rumble?: { gain: number };
  /** Random one-shots: average events per second, loudness, and when they happen. */
  events?: { kind: AmbientEvent; rate: number; gain: number; when?: 'day' | 'night' | 'always' }[];
  /** Echo on the events (0 = dry … ~0.5 = cavernous). */
  echo?: number;
}

const PENTATONIC = [0, 3, 5, 7, 10, 12, 15];
/** Time to the echo when nothing says otherwise (s). */
const DEFAULT_ECHO_DELAY = 0.27;

export class Ambience {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private events: GainNode | null = null;
  private windGain: GainNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  /** The echo's delay line, its feedback, and how much of it is heard. */
  private echoDelay: DelayNode | null = null;
  private echoFeedback: GainNode | null = null;
  private echoWet: GainNode | null = null;
  private echoNow = { amount: -1, delay: -1 };
  private noise: AudioBuffer | null = null;
  private gust = 0.5;
  private gustTarget = 0.5;
  private muted = false;
  private readonly volume = 0.7;

  constructor(private readonly def: AmbienceDef) {}

  get started(): boolean {
    return this.ctx !== null;
  }

  /** Create the audio graph. Must be called from a user gesture. */
  start(muted: boolean): void {
    if (this.ctx) return;
    const Ctx = window.AudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    this.ctx = ctx;
    // Started without a user gesture (e.g. from a gamepad): resume on the next one.
    if (ctx.state === 'suspended') {
      const resume = () => {
        void ctx.resume();
        window.removeEventListener('pointerdown', resume);
        window.removeEventListener('keydown', resume);
      };
      window.addEventListener('pointerdown', resume);
      window.addEventListener('keydown', resume);
    }
    this.muted = muted;
    const master = ctx.createGain();
    master.gain.value = muted ? 0 : this.volume;
    master.connect(ctx.destination);
    this.master = master;
    this.noise = makeNoise(ctx, 3, false);
    const d = this.def;

    if (d.wind) {
      const src = loop(ctx, this.noise);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = d.wind.cutoff;
      filter.Q.value = 0.6;
      const g = ctx.createGain();
      g.gain.value = d.wind.gain * 0.5;
      src.connect(filter).connect(g).connect(master);
      this.windGain = g;
      this.windFilter = filter;
    }
    if (d.rumble) {
      const src = loop(ctx, makeNoise(ctx, 4, true));
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 140;
      const g = ctx.createGain();
      g.gain.value = d.rumble.gain;
      src.connect(filter).connect(g).connect(master);
    }
    if (d.drone) {
      const g = ctx.createGain();
      g.gain.value = d.drone.gain;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 900;
      filter.connect(g).connect(master);
      // Slow tremolo so the pad breathes.
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.07;
      const depth = ctx.createGain();
      depth.gain.value = d.drone.gain * 0.5;
      lfo.connect(depth).connect(g.gain);
      lfo.start();
      for (const [i, f] of d.drone.freqs.entries()) {
        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.value = f;
        o.detune.value = (i % 2 ? 1 : -1) * 6;
        o.connect(filter);
        o.start();
      }
    }
    // Events go through an echo (silent unless the def or setEcho asks for one).
    const ev = ctx.createGain();
    ev.connect(master);
    const delay = ctx.createDelay(1);
    const fb = ctx.createGain();
    const tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = 2500;
    const wet = ctx.createGain();
    ev.connect(delay).connect(tone).connect(fb).connect(delay);
    tone.connect(wet).connect(master);
    this.echoDelay = delay;
    this.echoFeedback = fb;
    this.echoWet = wet;
    this.echoNow = { amount: -1, delay: -1 };
    this.setEcho(d.echo ?? 0, DEFAULT_ECHO_DELAY);
    this.events = ev;
  }

  /**
   * How much the events echo (0 = dry … ~0.6 = cavernous) and the time to the echo (s),
   * e.g. following the size of a cave's space. Glides there; cheap to call every frame.
   */
  setEcho(amount: number, delay: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.echoDelay || !this.echoFeedback || !this.echoWet) return;
    const now = this.echoNow;
    if (Math.abs(amount - now.amount) < 0.01 && Math.abs(delay - now.delay) < 0.005) return;
    now.amount = amount;
    now.delay = delay;
    const t = ctx.currentTime;
    this.echoFeedback.gain.setTargetAtTime(Math.min(amount, 0.75), t, 0.3);
    this.echoWet.gain.setTargetAtTime(Math.min(1, amount * 2), t, 0.3);
    this.echoDelay.delayTime.setTargetAtTime(Math.min(Math.max(delay, 0.02), 0.95), t, 0.5);
  }

  /** Fade out and release the audio graph (e.g. when walking into another world). */
  stop(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.master?.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
    setTimeout(() => void ctx.close(), 2000);
    this.ctx = null;
    this.master = null;
    this.events = null;
    this.windGain = null;
    this.windFilter = null;
    this.echoDelay = null;
    this.echoFeedback = null;
    this.echoWet = null;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.ctx)
      this.master.gain.setTargetAtTime(muted ? 0 : this.volume, this.ctx.currentTime, 0.1);
  }

  /** Per frame. `day`: 0 night … 1 day at the listener. */
  update(dt: number, day: number): void {
    const ctx = this.ctx;
    if (!ctx || this.muted) return;
    // Wind gusts: a slow random walk on loudness and brightness.
    if (this.windGain && this.windFilter && this.def.wind) {
      if (Math.random() < dt * 0.25) this.gustTarget = 0.25 + Math.random() * 0.75;
      this.gust += (this.gustTarget - this.gust) * Math.min(1, dt * 0.4);
      const w = this.def.wind;
      this.windGain.gain.setTargetAtTime(w.gain * this.gust, ctx.currentTime, 0.3);
      this.windFilter.frequency.setTargetAtTime(w.cutoff * (0.6 + this.gust * 0.8), ctx.currentTime, 0.3);
    }
    for (const e of this.def.events ?? []) {
      const weight = e.when === 'day' ? day : e.when === 'night' ? 1 - day : 1;
      if (Math.random() < e.rate * weight * dt) this.fire(e.kind, e.gain);
    }
  }

  private fire(kind: AmbientEvent, gain: number): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + 0.02;
    const pan = ctx.createStereoPanner();
    pan.pan.value = Math.random() * 1.6 - 0.8;
    pan.connect(this.events!);
    const env = (g: GainNode, peak: number, at: number, decay: number) => {
      g.gain.setValueAtTime(0, at);
      g.gain.linearRampToValueAtTime(peak, at + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, at + decay);
    };
    if (kind === 'bird') {
      // A short phrase of rising whistles.
      const base = 2200 + Math.random() * 1600;
      const n = 2 + Math.floor(Math.random() * 4);
      for (let i = 0; i < n; i++) {
        const at = t + i * (0.09 + Math.random() * 0.05);
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.setValueAtTime(base * (0.9 + Math.random() * 0.2), at);
        o.frequency.exponentialRampToValueAtTime(base * (1.3 + Math.random() * 0.3), at + 0.07);
        env(g, gain, at, 0.09);
        o.connect(g).connect(pan);
        o.start(at);
        o.stop(at + 0.12);
      }
    } else if (kind === 'cricket') {
      const f = 4200 + Math.random() * 600;
      for (let i = 0; i < 3; i++) {
        const at = t + i * 0.045;
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = f;
        env(g, gain, at, 0.03);
        o.connect(g).connect(pan);
        o.start(at);
        o.stop(at + 0.05);
      }
    } else if (kind === 'chime') {
      // A soft glassy bell on a pentatonic scale.
      const f = 660 * 2 ** (PENTATONIC[Math.floor(Math.random() * PENTATONIC.length)] / 12);
      for (const [ratio, amp] of [
        [1, 1],
        [2.76, 0.3],
        [5.4, 0.12],
      ]) {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = f * ratio;
        env(g, gain * amp, t, 2.8 / ratio ** 0.5);
        o.connect(g).connect(pan);
        o.start(t);
        o.stop(t + 3);
      }
    } else if (kind === 'crackle') {
      // A cluster of tiny noise pops.
      const n = 1 + Math.floor(Math.random() * 5);
      for (let i = 0; i < n; i++) {
        const at = t + Math.random() * 0.25;
        const src = ctx.createBufferSource();
        src.buffer = this.noise;
        const hp = ctx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 1200 + Math.random() * 2000;
        const g = ctx.createGain();
        env(g, gain * (0.4 + Math.random()), at, 0.02 + Math.random() * 0.03);
        src.connect(hp).connect(g).connect(pan);
        src.start(at, Math.random() * 2);
        src.stop(at + 0.06);
      }
    } else {
      // A water drip: a quick falling blip.
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      const f = 900 + Math.random() * 900;
      o.frequency.setValueAtTime(f, t);
      o.frequency.exponentialRampToValueAtTime(f * 0.45, t + 0.08);
      env(g, gain, t, 0.18);
      o.connect(g).connect(pan);
      o.start(t);
      o.stop(t + 0.2);
    }
  }
}

function makeNoise(ctx: AudioContext, seconds: number, brown: boolean): AudioBuffer {
  const buf = ctx.createBuffer(2, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const data = buf.getChannelData(c);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      if (brown) {
        last = (last + 0.02 * white) / 1.02;
        data[i] = last * 3.5;
      } else {
        data[i] = white * 0.5;
      }
    }
  }
  return buf;
}

function loop(ctx: AudioContext, buffer: AudioBuffer): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  src.start();
  return src;
}
